
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Product } from '@/types/portfolio';
import { CheckCircle, Building, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockChemicalSafetyData } from '@/data/mockProducts';
import { SortField, SortDirection } from './DirectoryResults';

interface DirectoryProductTableProps {
  products: Product[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

export const DirectoryProductTable = ({ 
  products, 
  sortField, 
  sortDirection, 
  onSort 
}: DirectoryProductTableProps) => {
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const getConcernColor = (concern: string) => {
    switch (concern) {
      case 'low': return 'text-green-600';
      case 'moderate': return 'text-amber-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getWarningIcon = (type: string) => {
    const iconClasses = "px-1.5 py-0.5 rounded text-xs font-bold text-white mr-1";
    switch (type) {
      case 'carcinogen':
        return <span className={`${iconClasses} bg-red-600`} title="May cause cancer">CANCER</span>;
      case 'reproductive':
        return <span className={`${iconClasses} bg-amber-800`} title="May harm fertility or unborn child">REPRO</span>;
      case 'environment':
        return <span className={`${iconClasses} bg-green-600`} title="Harmful to aquatic life">AQUATIC</span>;
      case 'skin':
        return <span className={`${iconClasses} bg-orange-600`} title="May cause skin irritation">SKIN</span>;
      default:
        return null;
    }
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Button
                variant="ghost"
                onClick={() => onSort('name')}
                className="h-auto p-0 font-medium"
              >
                Product Name
                {getSortIcon('name')}
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                onClick={() => onSort('manufacturer')}
                className="h-auto p-0 font-medium"
              >
                Manufacturer
                {getSortIcon('manufacturer')}
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                onClick={() => onSort('product_type')}
                className="h-auto p-0 font-medium"
              >
                Type
                {getSortIcon('product_type')}
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                onClick={() => onSort('certification_status')}
                className="h-auto p-0 font-medium"
              >
                Status
                {getSortIcon('certification_status')}
              </Button>
            </TableHead>
            <TableHead>Chemical Safety</TableHead>
            <TableHead>Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => {
            const safetyData = mockChemicalSafetyData[product.id as keyof typeof mockChemicalSafetyData] || {
              concernLevel: 'moderate' as const,
              warningIcons: ['skin', 'environment'] as const
            };

            return (
              <TableRow key={product.id} className="hover:bg-muted/50">
                <TableCell>
                  <Link 
                    to={`/directory/products/${product.id}`}
                    className="font-medium hover:underline text-primary"
                  >
                    {product.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {product.manufacturer && (
                    <div className="flex items-center gap-1">
                      <Building className="h-3 w-3 text-muted-foreground" />
                      {product.manufacturer}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {product.product_type?.replace('_', ' ') || 'Product'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Certified
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 text-muted-foreground" />
                      <span className={`text-xs font-semibold ${getConcernColor(safetyData.concernLevel)}`}>
                        {safetyData.concernLevel.toUpperCase()}
                      </span>
                    </div>
                    {safetyData.warningIcons.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {safetyData.warningIcons.map((icon, index) => (
                          <span key={index}>
                            {getWarningIcon(icon)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-xs">
                  {product.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {product.description}
                    </p>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
